"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface PageLoaderProps {
  text?: string;
  fullScreen?: boolean;
}

const colors = {
  primary: "#136207",
  primaryDark: "#0f4a05",
  primaryLight: "#88cf07",
  white: "#FFFFFF",
  gold: "#FDB813",
};

const loadingMessages = [
  "Loading...",
  "Preparing your experience...",
  "Almost there...",
  "Just a moment...",
  "Getting things ready...",
];

const LeafIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <motion.path
      d="M12 2C12 2 6 8 6 14C6 17.3 8.7 20 12 20C15.3 20 18 17.3 18 14C18 8 12 2 12 2Z"
      fill={colors.primaryLight}
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.path
      d="M12 6C12 6 9 10 9 13C9 14.7 10.3 16 12 16C13.7 16 15 14.7 15 13C15 10 12 6 12 6Z"
      fill={colors.white}
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 1.5, delay: 0.3, repeat: Infinity, ease: "easeInOut" }}
    />
  </svg>
);

export default function PageLoader({
  text = "Loading...",
  fullScreen = true,
}: PageLoaderProps) {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  if (!fullScreen) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          {/* Spinning rings */}
          <div className="relative h-16 w-16">
            <motion.div
              className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#136207]"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute inset-2 rounded-full border-4 border-transparent border-t-[#88cf07]"
              animate={{ rotate: -360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute inset-4 rounded-full border-4 border-transparent border-t-[#FDB813]"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-sm font-medium text-[#136207]"
          >
            {loadingMessages[messageIndex]}
          </motion.p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex min-h-screen w-full flex-col items-center justify-center bg-gradient-to-br from-[#f0f8ec] via-white to-[#e8f5e0]">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute h-3 w-3 rounded-full"
            style={{
              backgroundColor: i % 2 === 0 ? colors.primary : colors.primaryLight,
              opacity: 0.1,
            }}
            initial={{
              x: Math.random() * 100 - 50 + "vw",
              y: Math.random() * 100 - 50 + "vh",
              scale: 0,
            }}
            animate={{
              x: [
                Math.random() * 100 - 50 + "vw",
                Math.random() * 100 - 50 + "vw",
                Math.random() * 100 - 50 + "vw",
              ],
              y: [
                Math.random() * 100 - 50 + "vh",
                Math.random() * 100 - 50 + "vh",
                Math.random() * 100 - 50 + "vh",
              ],
              scale: [0, 1.5, 0],
              opacity: [0, 0.15, 0],
            }}
            transition={{
              duration: 4 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.6,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Main loader content */}
      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Logo / Brand mark */}
        <motion.div
          className="relative"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          {/* Outer glow */}
          <motion.div
            className="absolute -inset-8 rounded-full bg-gradient-to-r from-[#136207]/20 via-[#88cf07]/20 to-[#136207]/20 blur-2xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.3, 0.5],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Logo icon */}
          <div className="relative flex h-24 w-24 items-center justify-center">
            <motion.div
              className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#136207] to-[#0f4a05] shadow-xl"
              animate={{
                rotate: [0, 5, 0, -5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="flex h-full w-full items-center justify-center">
                <motion.span
                  className="text-4xl font-bold text-white"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  Y
                </motion.span>
              </div>
            </motion.div>

            {/* Decorative leaf */}
            <motion.div
              className="absolute -right-3 -top-3"
              animate={{
                rotate: [0, 15, 0, -15, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <LeafIcon className="h-8 w-8" />
            </motion.div>
          </div>
        </motion.div>

        {/* Company name */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-[#136207] sm:text-3xl">
            Yegna Microfinance
          </h2>
          <p className="mt-1 text-sm text-[#136207]/60">S.C.</p>
        </motion.div>

        {/* Animated spinner */}
        <div className="relative mt-4 h-12 w-12">
          {/* Outer ring */}
          <motion.div
            className="absolute inset-0 rounded-full border-[3px] border-[#136207]/20"
            style={{ borderTopColor: colors.primary }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          {/* Middle ring */}
          <motion.div
            className="absolute inset-1 rounded-full border-[3px] border-[#88cf07]/20"
            style={{ borderTopColor: colors.primaryLight }}
            animate={{ rotate: -360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          />
          {/* Inner ring */}
          <motion.div
            className="absolute inset-2 rounded-full border-[3px] border-[#FDB813]/20"
            style={{ borderTopColor: colors.gold }}
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
          {/* Center dot */}
          <motion.div
            className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#136207]"
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        {/* Loading message */}
        <div className="h-8">
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-sm font-medium text-[#136207]/80"
          >
            {loadingMessages[messageIndex]}
          </motion.p>
        </div>

        {/* Progress bar */}
        <div className="h-1 w-48 overflow-hidden rounded-full bg-[#136207]/10">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#136207] via-[#88cf07] to-[#FDB813]"
            animate={{
              x: ["-100%", "200%"],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
      </div>

      {/* Bottom decorative gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#136207]/5 to-transparent" />

      {/* Decorative corner elements */}
      <motion.div
        className="absolute left-8 top-8 h-12 w-12 rounded-lg border border-[#136207]/10"
        animate={{ rotate: [0, 90], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute right-8 top-8 h-12 w-12 rounded-lg border border-[#88cf07]/10"
        animate={{ rotate: [0, -90], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute bottom-8 left-8 h-12 w-12 rounded-lg border border-[#88cf07]/10"
        animate={{ rotate: [0, -90], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute bottom-8 right-8 h-12 w-12 rounded-lg border border-[#136207]/10"
        animate={{ rotate: [0, 90], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
}
